import * as React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { TimeFrameOptions } from 'pages/Dashboard/utils/trackingUtils';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import theme, { grey } from 'theme';
import { GoogleDate } from 'utils/dateUtils';

interface Range {
  maxScore: number;
  text: string;
}

interface TimeseriesResult {
  score: number;
  attemptedAt: string;
  date?: GoogleDate;
}

interface AreaChartProps {
  data: TimeseriesResult[];
  ranges: Range[];
  timeFrame?: string;
}

// For equidistant y-axis: domain is [0, ranges.length - 1]
function getYAxisDomain(ranges: Range[]) {
  if (!ranges?.length) return [0, 1];
  return [0, ranges.length - 1];
}

// For equidistant y-axis: map each range to a normalized index (0, 1, 2, ...)
function getYAxisTicks(ranges: Range[]) {
  if (!ranges?.length) return [];
  return Array.from({ length: ranges.length }, (_, i) => i);
}

// For equidistant y-axis: label is just the range text at index
function getYAxisTickFormatter(ranges: Range[]) {
  return (idx: number) => (ranges[idx] ? ranges[idx].text : '');
}

function getScoreLabel(score: number, ranges: Range[]) {
  const found = ranges.find((r) => score <= r.maxScore);
  return found ? found.text : '';
}

function formatDate(
  attemptedAt: string,
  dateObj?: GoogleDate,
  dateFormat = 'dd/MM',
) {
  if (dateObj && dateObj.year && dateObj.month && dateObj.day) {
    return format(new Date(dateObj.year, dateObj.month - 1, dateObj.day), dateFormat);
  }
  if (attemptedAt) {
    return format(new Date(attemptedAt), dateFormat);
  }
  return '';
}

// Helper to get x-axis labels and ticks based on timeFrame
function getXAxisLabelsAndTicks(timeFrame: string, data: TimeseriesResult[]) {
  if (!data || !data.length) return { labels: [], ticks: [] };
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let labels: string[] = [];
  let ticks: string[] = [];
  if (timeFrame === TimeFrameOptions.LAST_7_DAYS) {
    // Always plot all 7 days (Sunday to Saturday), only latest entry per day
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // go to last Sunday
    labels = daysOfWeek;
    ticks = daysOfWeek;
    // For each day, only keep the latest entry
    const latestByDay: Record<string, TimeseriesResult> = {};
    data.forEach((item) => {
      const d = item.date
        ? new Date(item.date.year, item.date.month - 1, item.date.day)
        : new Date(item.attemptedAt);
      const key = daysOfWeek[d.getDay()];
      if (
        !latestByDay[key]
        || new Date(item.attemptedAt) > new Date(latestByDay[key].attemptedAt)
      ) {
        latestByDay[key] = item;
      }
    });
    const mappedData = daysOfWeek.map((day) => {
      const item = latestByDay[day];
      return item ? { ...item, dateLabel: day } : { dateLabel: day, score: null };
    });
    return {
      labels,
      ticks,
      mappedData,
      mapData: (item: TimeseriesResult) => item, // not used, we use mappedData
    };
  }
  else if (timeFrame === TimeFrameOptions.LAST_30_DAYS) {
    // Plot only Sundays as Week 1, Week 2, etc., only latest entry per week
    const now = new Date();
    const sundays: Date[] = [];
    // Find the last 5 Sundays (to cover 30 days)
    const d = new Date(now);
    d.setDate(d.getDate() - d.getDay()); // go to last Sunday
    for (let i = 0; i < 5; i += 1) {
      sundays.unshift(new Date(d));
      d.setDate(d.getDate() - 7);
    }
    labels = sundays.map((_, idx) => `Week ${idx + 1}`);
    ticks = labels;
    // For each week, only keep the latest entry (on or after that Sunday, before next Sunday)
    const latestByWeek: Record<string, TimeseriesResult> = {};
    data.forEach((item) => {
      const itemDate = item.date
        ? new Date(item.date.year, item.date.month - 1, item.date.day)
        : new Date(item.attemptedAt);
      for (let i = 0; i < sundays.length; i += 1) {
        const weekStart = sundays[i];
        const weekEnd = i < sundays.length - 1
          ? sundays[i + 1]
          : new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
        if (itemDate >= weekStart && itemDate < weekEnd) {
          const key = `Week ${i + 1}`;
          if (
            !latestByWeek[key]
            || new Date(item.attemptedAt) > new Date(latestByWeek[key].attemptedAt)
          ) {
            latestByWeek[key] = item;
          }
        }
      }
    });
    const mappedData = labels.map((week) => {
      const item = latestByWeek[week];
      return item ? { ...item, dateLabel: week } : { dateLabel: week, score: null };
    });
    return {
      labels,
      ticks,
      mappedData,
      mapData: (item: TimeseriesResult) => item, // not used, we use mappedData
    };
  }
  else if (timeFrame === TimeFrameOptions.LAST_3_MONTHS) {
    // Last 3 months, show all months as labels, only latest entry per month
    const now = new Date();
    labels = [];
    const monthKeys: string[] = [];
    for (let i = 2; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      labels.push(months[d.getMonth()]);
      monthKeys.push(`${d.getFullYear()}-${d.getMonth() + 1}`);
    }
    ticks = labels;
    // For each month, only keep the latest entry
    const latestByMonth: Record<string, TimeseriesResult> = {};
    data.forEach((item) => {
      const d = item.date
        ? new Date(item.date.year, item.date.month - 1, item.date.day)
        : new Date(item.attemptedAt);
      const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      if (
        !latestByMonth[key]
        || new Date(item.attemptedAt) > new Date(latestByMonth[key].attemptedAt)
      ) {
        latestByMonth[key] = item;
      }
    });
    const mappedData = monthKeys.map((key, idx) => {
      const item = latestByMonth[key];
      return item
        ? { ...item, dateLabel: labels[idx] }
        : { dateLabel: labels[idx], score: null };
    });
    return {
      labels,
      ticks,
      mappedData,
      mapData: (item: TimeseriesResult) => item, // not used, we use mappedData
    };
  }
  else if (timeFrame === TimeFrameOptions.LAST_6_MONTHS) {
    // Last 6 months, show all months as labels, only latest entry per month
    const now = new Date();
    labels = [];
    const monthKeys: string[] = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      labels.push(months[d.getMonth()]);
      monthKeys.push(`${d.getFullYear()}-${d.getMonth() + 1}`);
    }
    ticks = labels;
    // For each month, only keep the latest entry
    const latestByMonth: Record<string, TimeseriesResult> = {};
    data.forEach((item) => {
      const d = item.date
        ? new Date(item.date.year, item.date.month - 1, item.date.day)
        : new Date(item.attemptedAt);
      const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      if (
        !latestByMonth[key]
        || new Date(item.attemptedAt) > new Date(latestByMonth[key].attemptedAt)
      ) {
        latestByMonth[key] = item;
      }
    });
    const mappedData = monthKeys.map((key, idx) => {
      const item = latestByMonth[key];
      return item
        ? { ...item, dateLabel: labels[idx] }
        : { dateLabel: labels[idx], score: null };
    });
    return {
      labels,
      ticks,
      mappedData,
      mapData: (item: TimeseriesResult) => item, // not used, we use mappedData
    };
  }
  // Default: just use attemptedAt
  return {
    ticks: data.map((item) => item.attemptedAt),
    mapData: (item: TimeseriesResult) => ({ ...item, dateLabel: item.attemptedAt }),
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      score: number;
      rawScore?: number;
      date: GoogleDate;
      attemptedAt: string;
    };
  }>;
  ranges: Range[];
}

function CustomTooltip({ active, payload, ranges }: CustomTooltipProps) {
  if (active && payload && payload.length) {
    const { rawScore, score, date, attemptedAt } = payload[0].payload;
    const displayScore = typeof rawScore === 'number' ? rawScore : score;
    const label = formatDate(attemptedAt, date, 'EEE, MMM d');
    const scoreLabel = getScoreLabel(displayScore, ranges);
    return (
      <Box
        sx={{
          background: '#e3f2fd',
          borderRadius: 2,
          boxShadow: 2,
          px: 2,
          py: 1,
          minWidth: 120,
        }}
      >
        <Typography variant='caption' color='text.primary' component='p' mb={0.5}>
          {label}
        </Typography>
        <Typography variant='caption' color='text.primary'>
          <span>Score:</span>
          {' '}
          <span>{displayScore}</span>
          {scoreLabel && (
            <span>
              {' '}
              (
              {scoreLabel}
              )
            </span>
          )}
        </Typography>
      </Box>
    );
  }
  return null;
}

function RatingScaleAreaChart({ data, ranges, timeFrame }: AreaChartProps) {
  // Get x-axis labels and ticks logic
  const { ticks, mappedData } = React.useMemo(() => getXAxisLabelsAndTicks(timeFrame || '', data), [timeFrame, data]);
  // Map scores to normalized y-index for equidistant plotting
  const chartData = React.useMemo(() => {
    // Move getScoreIndex inside useMemo for correct dependency tracking
    const getScoreIndex = (score: number) => {
      if (!ranges?.length) return 0;
      for (let i = 0; i < ranges.length; i += 1) {
        if (score <= ranges[i].maxScore) return i;
      }
      return ranges.length - 1;
    };
    // mappedData may have items with only dateLabel and score: null
    type MappedDataItem =
      | (TimeseriesResult & { dateLabel?: string })
      | { dateLabel: string; score: null };
    const addRawScore = (item: MappedDataItem) => {
      if (typeof item.score === 'number') {
        return {
          ...item,
          score: getScoreIndex(item.score),
          rawScore: item.score,
        };
      }
      // For items with score: null (placeholders for missing data)
      return {
        ...item,
        score: null,
        rawScore: null,
      };
    };
    if (mappedData) {
      return mappedData.map(addRawScore);
    }
    if (!data) return [];
    return data.map(addRawScore);
  }, [data, mappedData, ranges]);

  const gradientId = 'ratingScaleAreaGradient';

  return (
    <ResponsiveContainer width='100%' height={260}>
      <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='5%' stopColor='#6366f1' stopOpacity={0.8} />
            <stop offset='65%' stopColor='#6366f1' stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey='dateLabel'
          fontSize={10}
          color={theme.palette.text.secondary}
          ticks={ticks}
        />
        <YAxis
          domain={getYAxisDomain(ranges)}
          ticks={getYAxisTicks(ranges)}
          tickFormatter={getYAxisTickFormatter(ranges)}
          fontSize={8}
          width={55}
          tick={{ fill: theme.palette.text.secondary }}
          stroke={grey[50]}
        />
        <Tooltip content={<CustomTooltip ranges={ranges} />} />
        <CartesianGrid vertical={false} stroke={grey[50]} />
        <Area
          type='monotone'
          dataKey='score'
          stroke='#4338ca'
          fill={`url(#${gradientId})`}
          fillOpacity={1}
          dot
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default RatingScaleAreaChart;
